/* ==========================================================
   ESSENCE SEARCH V5 — NATIVE-FIRST CANDIDATE FUSION
   Chez Mishel / Shopify Prestige

   Invariants:
   1. Shopify native search is always the fail-open candidate base.
   2. Enrichment only adds title/tag candidates; it never removes native ones.
   3. Hebrew morphology and context are ranking signals, never hard filters.
   4. Numeric and Latin alphanumeric identifiers stay fully native.
   5. Preview and full results use Shopify's locale-supported /search fallback.
   6. No global fetch interception and no buyer-facing technical query.
========================================================== */

(() => {
  'use strict';

  if (window.__essenceSearchV5Loaded) return;
  window.__essenceSearchV5Loaded = true;

  const SEARCH_PATH_RE = /\/search\/?$/u;
  const SECTION_SELECTOR = '.shopify-section--main-search';
  const PRODUCT_LIST_SELECTOR = 'product-list[id^="product-list-"]';
  const PRODUCT_ITEM_SELECTOR = '.essence-search-sort-item';
  const PREVIEW_LIST_SELECTOR = '.predictive-search__products';
  const PREVIEW_ITEM_SELECTOR = '.essence-search-preview-item';
  const SORT_STATUS_CLASS = 'essence-search-sort-status';
  const STOREFRONT_API_URL = '/api/2026-07/graphql.json';

  const MAX_QUERY_LENGTH = 180;
  const MAX_TERMS = 12;
  const MAX_FIELD_LENGTH = 2000;
  const MAX_FIELD_TOKENS = 160;
  const MAX_TECHNICAL_QUERY_LENGTH = 4000;
  const MAX_ENRICHMENT_PAGES = 2;
  const PRODUCTS_PER_PAGE = 48;
  const MIN_PREFIX_LENGTH = 3;
  const BEST_SELLING_CACHE_TTL = 60 * 60 * 1000;

  const CONTEXT_SORTS = new Set(['manual', 'relevance']);
  const GLOBAL_SORTS = new Set([
    'best-selling',
    'title-ascending',
    'title-descending',
    'created-ascending',
    'created-descending'
  ]);

  const candidateMarkupCache = new Map();
  const previewCandidateMarkupCache = new Map();
  const bestSellingPromiseCache = new Map();
  const activeGlobalSortPromises = new WeakMap();
  let scheduleTimer = null;
  let lifecycleVersion = 0;

  const collator = new Intl.Collator(['he', 'en'], {
    numeric: true,
    sensitivity: 'base'
  });

  const normalizeWhitespace = (value) =>
    String(value || '').replace(/\s+/gu, ' ').trim();

  const normalizeHebrewFinals = (value) =>
    String(value || '')
      .replace(/ך/gu, 'כ')
      .replace(/ם/gu, 'מ')
      .replace(/ן/gu, 'נ')
      .replace(/ף/gu, 'פ')
      .replace(/ץ/gu, 'צ');

  const restoreHebrewFinal = (value) =>
    String(value || '').replace(/[כמנפצ]$/u, (letter) => {
      const finalByRegular = {
        כ: 'ך',
        מ: 'ם',
        נ: 'ן',
        פ: 'ף',
        צ: 'ץ'
      };

      return finalByRegular[letter] || letter;
    });

  const normalizeToken = (value) =>
    normalizeHebrewFinals(
      String(value || '')
        .toLocaleLowerCase('he')
        .replace(/["״'’׳]/gu, '')
        .replace(/[^\p{L}\p{N}]+/gu, '')
    );

  const tokenize = (value) => {
    const normalized = normalizeWhitespace(value)
      .slice(0, MAX_QUERY_LENGTH)
      .toLocaleLowerCase('he')
      .replace(/["״'’׳]/gu, '')
      .replace(/(\p{L})(\p{N})/gu, '$1 $2')
      .replace(/(\p{N})(\p{L})/gu, '$1 $2');

    return (normalized.match(/[\p{L}\p{N}]+/gu) || [])
      .map(normalizeToken)
      .filter(Boolean)
      .slice(0, MAX_TERMS);
  };

  const tokenizeField = (value) => {
    const normalized = String(value || '')
      .slice(0, MAX_FIELD_LENGTH)
      .toLocaleLowerCase('he')
      .replace(/["״'’׳]/gu, '')
      .replace(/(\p{L})(\p{N})/gu, '$1 $2')
      .replace(/(\p{N})(\p{L})/gu, '$1 $2');

    return (normalized.match(/[\p{L}\p{N}]+/gu) || [])
      .map(normalizeToken)
      .filter(Boolean)
      .slice(0, MAX_FIELD_TOKENS);
  };

  const getBuyerQuery = () => {
    try {
      return normalizeWhitespace(
        new URL(window.location.href).searchParams.get('q')
      );
    } catch (error) {
      return '';
    }
  };

  const getPreviewQuery = () =>
    normalizeWhitespace(
      document.querySelector(
        '#predictive-search-form input[name="q"]'
      )?.value
    );

  const getActiveSort = () => {
    try {
      return (
        new URL(window.location.href).searchParams.get('sort_by') ||
        'relevance'
      );
    } catch (error) {
      return 'relevance';
    }
  };

  const isIdentifierQuery = (query) => {
    const normalized = normalizeWhitespace(query);

    if (!normalized || !/\d/u.test(normalized)) return false;

    const pureNumeric = /^[\d\s._\-/]+$/u.test(normalized);
    const latinCode = /^[A-Za-z\d._\-/]+$/u.test(normalized);

    return pureNumeric || latinCode;
  };

  const buildVariants = (rawTerm) => {
    const raw = String(rawTerm || '')
      .toLocaleLowerCase('he')
      .replace(/["״'’׳]/gu, '')
      .replace(/[^\p{L}\p{N}]+/gu, '');
    const normalized = normalizeToken(raw);
    const variants = new Set([raw, normalized]);

    if (/^[\u0590-\u05FF]+$/u.test(raw)) {
      if (normalized.length >= 4 && normalized.endsWith('ה')) {
        const stem = normalized.slice(0, -1);
        variants.add(stem);
        variants.add(restoreHebrewFinal(stem));
      }

      if (normalized.length >= 4 && normalized.endsWith('ימ')) {
        const stem = normalized.slice(0, -2);
        variants.add(stem);
        variants.add(restoreHebrewFinal(stem));
      }

      if (normalized.length >= 4 && normalized.endsWith('ות')) {
        const stem = normalized.slice(0, -2);
        variants.add(stem);
        variants.add(restoreHebrewFinal(stem));
        variants.add(`${stem}ת`);
        variants.add(`${stem}ה`);
      }
    }

    return Array.from(variants)
      .map((variant) => String(variant || '').trim())
      .filter(Boolean);
  };

  const createModels = (query) =>
    tokenize(query).map((term) => ({
      numeric: /^\d+$/u.test(term),
      variants: buildVariants(term).map(normalizeToken).filter(Boolean)
    }));

  const modelMatchesToken = (model, token) => {
    if (!model || !token) return false;

    return model.variants.some((variant) => {
      if (!variant) return false;
      if (token === variant) return true;
      if (model.numeric) return false;

      return (
        variant.length >= MIN_PREFIX_LENGTH &&
        token.startsWith(variant)
      );
    });
  };

  const allModelsMatchDistinctTokens = (models, tokens) => {
    const used = new Set();

    for (const model of models) {
      let matchedIndex = -1;

      for (let index = 0; index < tokens.length; index += 1) {
        if (used.has(index)) continue;

        if (modelMatchesToken(model, tokens[index])) {
          matchedIndex = index;
          break;
        }
      }

      if (matchedIndex < 0) return false;
      used.add(matchedIndex);
    }

    return true;
  };

  const hasConsecutiveMatch = (models, tokens) => {
    if (!models.length || tokens.length < models.length) return false;

    for (
      let start = 0;
      start <= tokens.length - models.length;
      start += 1
    ) {
      let matched = true;

      for (let offset = 0; offset < models.length; offset += 1) {
        if (!modelMatchesToken(models[offset], tokens[start + offset])) {
          matched = false;
          break;
        }
      }

      if (matched) return true;
    }

    return false;
  };

  const hasAdjacentSetMatch = (models, tokens) => {
    if (!models.length || tokens.length < models.length) return false;

    for (
      let start = 0;
      start <= tokens.length - models.length;
      start += 1
    ) {
      const windowTokens = tokens.slice(start, start + models.length);

      if (allModelsMatchDistinctTokens(models, windowTokens)) {
        return true;
      }
    }

    return false;
  };

  const getOrderedSpan = (models, tokens) => {
    if (!models.length) return Number.POSITIVE_INFINITY;

    let cursor = 0;
    let firstIndex = -1;
    let lastIndex = -1;

    for (const model of models) {
      let foundIndex = -1;

      while (cursor < tokens.length) {
        if (modelMatchesToken(model, tokens[cursor])) {
          foundIndex = cursor;
          cursor += 1;
          break;
        }

        cursor += 1;
      }

      if (foundIndex < 0) return Number.POSITIVE_INFINITY;
      if (firstIndex < 0) firstIndex = foundIndex;
      lastIndex = foundIndex;
    }

    return lastIndex - firstIndex + 1;
  };

  const getCanonicalNumericContext = (models, tokens) => {
    const numericModels = models.filter((model) => model.numeric);
    const textModels = models.filter((model) => !model.numeric);

    if (numericModels.length !== 1 || !textModels.length) {
      return null;
    }

    const numericModel = numericModels[0];
    const isNumericToken = (token) => /^\d+$/u.test(token);

    for (let start = 0; start < tokens.length; start += 1) {
      if (!modelMatchesToken(textModels[0], tokens[start])) continue;

      const positions = [start];
      let cursor = start + 1;
      let allTextTermsFound = true;

      for (let index = 1; index < textModels.length; index += 1) {
        let foundAt = -1;

        while (cursor < tokens.length) {
          if (modelMatchesToken(textModels[index], tokens[cursor])) {
            foundAt = cursor;
            cursor += 1;
            break;
          }

          cursor += 1;
        }

        if (foundAt < 0) {
          allTextTermsFound = false;
          break;
        }

        positions.push(foundAt);
      }

      if (!allTextTermsFound) continue;

      const firstTextIndex = positions[0];
      const lastTextIndex = positions[positions.length - 1];
      const followingNumbers = [];

      for (
        let index = lastTextIndex + 1;
        index < tokens.length && index <= lastTextIndex + 6;
        index += 1
      ) {
        if (isNumericToken(tokens[index])) {
          followingNumbers.push(index);
        }
      }

      if (followingNumbers.length) {
        const closestFollowingNumber = followingNumbers[0];

        if (
          modelMatchesToken(
            numericModel,
            tokens[closestFollowingNumber]
          )
        ) {
          return {
            band: 0,
            span: closestFollowingNumber - firstTextIndex + 1
          };
        }

        // A different number immediately following the text term owns
        // that context (for example: "...24, סוטאז 28").
        continue;
      }

      for (
        let index = firstTextIndex - 1;
        index >= 0 && index >= firstTextIndex - 3;
        index -= 1
      ) {
        if (
          isNumericToken(tokens[index]) &&
          modelMatchesToken(numericModel, tokens[index])
        ) {
          return {
            band: 1,
            span: lastTextIndex - index + 1
          };
        }
      }
    }

    return null;
  };

  const getMatchQuality = (models, tokens) => {
    if (!models.length) return { band: 0, span: 0 };
    if (!tokens.length) return { band: 9, span: 999 };

    if (hasConsecutiveMatch(models, tokens)) {
      return { band: 0, span: models.length };
    }

    const hasNumericModel = models.some((model) => model.numeric);

    if (hasNumericModel) {
      const numericContext = getCanonicalNumericContext(
        models,
        tokens
      );

      if (numericContext) return numericContext;
    } else if (hasAdjacentSetMatch(models, tokens)) {
      return { band: 0, span: models.length };
    }

    const orderedSpan = getOrderedSpan(models, tokens);

    if (Number.isFinite(orderedSpan)) {
      return { band: 1, span: orderedSpan };
    }

    if (allModelsMatchDistinctTokens(models, tokens)) {
      return { band: 2, span: tokens.length };
    }

    return { band: 9, span: 999 };
  };

  const getContextScore = (item, query) => {
    if (!(item instanceof HTMLElement)) return 9999;

    const models = createModels(query);
    if (!models.length) return 0;

    const titleQuality = getMatchQuality(
      models,
      tokenizeField(item.dataset.essenceSearchTitle)
    );
    const tagsQuality = getMatchQuality(
      models,
      tokenizeField(item.dataset.essenceSearchTags)
    );

    const titleScore =
      titleQuality.band < 9
        ? titleQuality.band * 100 + titleQuality.span
        : 9999;
    const tagsScore =
      tagsQuality.band < 9
        ? tagsQuality.band * 100 + tagsQuality.span + 35
        : 9999;

    return Math.min(titleScore, tagsScore, 900);
  };

  const getProductList = () =>
    document.querySelector(
      `${SECTION_SELECTOR} ${PRODUCT_LIST_SELECTOR}`
    );

  const getProductItems = (productList = getProductList()) =>
    productList instanceof HTMLElement
      ? Array.from(productList.children).filter((item) =>
          item.classList.contains('essence-search-sort-item')
        )
      : [];

  const getOriginalIndex = (item) => {
    const value = Number(item.dataset.essenceSearchOriginalIndex);
    return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
  };

  const reorder = (container, items) => {
    if (!(container instanceof HTMLElement) || !items.length) return;

    const fragment = document.createDocumentFragment();
    items.forEach((item) => fragment.appendChild(item));
    container.appendChild(fragment);
  };

  const restoreNativeOrder = () => {
    const productList = getProductList();
    const items = getProductItems(productList);

    items.sort((a, b) => getOriginalIndex(a) - getOriginalIndex(b));
    reorder(productList, items);
  };

  const applyContextRanking = (
    query = getBuyerQuery(),
    sortValue = getActiveSort()
  ) => {
    if (
      !CONTEXT_SORTS.has(sortValue) ||
      isIdentifierQuery(query)
    ) {
      return;
    }

    const productList = getProductList();
    const items = getProductItems(productList);

    if (!(productList instanceof HTMLElement) || items.length < 2) {
      return;
    }

    const decorated = items.map((item, currentIndex) => ({
      item,
      currentIndex,
      score: getContextScore(item, query)
    }));

    decorated.sort(
      (a, b) => a.score - b.score || a.currentIndex - b.currentIndex
    );
    reorder(
      productList,
      decorated.map((entry) => entry.item)
    );
  };

  const rankPreview = () => {
    const query = getPreviewQuery();

    if (!query || isIdentifierQuery(query)) return;

    document.querySelectorAll(PREVIEW_LIST_SELECTOR).forEach((list) => {
      const items = Array.from(list.children).filter((item) =>
        item.classList.contains(
          PREVIEW_ITEM_SELECTOR.replace(/^\./u, '')
        )
      );

      if (items.length < 2) return;

      const decorated = items.map((item, currentIndex) => ({
        item,
        currentIndex,
        score: getContextScore(item, query)
      }));

      decorated.sort(
        (a, b) =>
          a.score - b.score || a.currentIndex - b.currentIndex
      );
      reorder(
        list,
        decorated.map((entry) => entry.item)
      );

      const visibleHandles = new Set();

      Array.from(list.children).forEach((item) => {
        const handle = item.dataset.essenceSearchHandle || '';

        if (!handle) return;

        if (!visibleHandles.has(handle) && visibleHandles.size < 5) {
          visibleHandles.add(handle);
        }

        if (!visibleHandles.has(handle)) {
          item.remove();
        }
      });
    });
  };

  const escapeSearchPhrase = (value) =>
    String(value || '')
      .replace(/\\/gu, '\\\\')
      .replace(/"/gu, '\\"');

  const escapeSearchToken = (value) =>
    String(value || '').replace(/([:\\()])/gu, '\\$1');

  const buildFieldVariantClause = (variant) => {
    const value = String(variant || '').trim();
    if (!value) return '';

    if (/^\d+$/u.test(value) || [...value].length < MIN_PREFIX_LENGTH) {
      const phrase = escapeSearchPhrase(value);
      return `(title:"${phrase}" OR tag:"${phrase}")`;
    }

    const token = escapeSearchToken(value);
    return `(title:${token}* OR tag:${token}*)`;
  };

  const buildTermClause = (term) => {
    const clauses = Array.from(
      new Set(buildVariants(term).map(buildFieldVariantClause))
    ).filter(Boolean);

    if (!clauses.length) return '';
    return clauses.length === 1
      ? clauses[0]
      : `(${clauses.join(' OR ')})`;
  };

  const buildCandidateQuery = (query) => {
    const cleanQuery = normalizeWhitespace(query).slice(
      0,
      MAX_QUERY_LENGTH
    );
    const terms = tokenize(cleanQuery);

    if (
      terms.length < 2 ||
      isIdentifierQuery(cleanQuery)
    ) {
      return '';
    }

    const exactPhrase = escapeSearchPhrase(cleanQuery);
    const allTerms = terms.map(buildTermClause).filter(Boolean);

    if (allTerms.length !== terms.length) return '';

    const technicalQuery =
      `((title:"${exactPhrase}" OR tag:"${exactPhrase}") OR ` +
      `(${allTerms.join(' AND ')}))`;

    return technicalQuery.length <= MAX_TECHNICAL_QUERY_LENGTH
      ? technicalQuery
      : '';
  };

  const getRootUrl = (path) => {
    const root =
      window.Shopify?.routes?.root &&
      typeof window.Shopify.routes.root === 'string'
        ? window.Shopify.routes.root
        : '/';
    const cleanRoot = root.endsWith('/') ? root : `${root}/`;

    return new URL(`${cleanRoot}${path.replace(/^\//u, '')}`, window.location.origin);
  };

  const fetchCandidateMarkup = (buyerQuery, sectionId) => {
    const technicalQuery = buildCandidateQuery(buyerQuery);
    if (!technicalQuery || !sectionId) return Promise.resolve([]);

    const cacheKey = `${sectionId}\n${technicalQuery}`;

    if (candidateMarkupCache.has(cacheKey)) {
      return candidateMarkupCache.get(cacheKey);
    }

    const requestPromise = (async () => {
      const markup = [];
      const seenHandles = new Set();

      for (
        let page = 1;
        page <= MAX_ENRICHMENT_PAGES;
        page += 1
      ) {
        const url = getRootUrl('search');
        url.searchParams.set('q', technicalQuery);
        url.searchParams.set('section_id', sectionId);
        url.searchParams.set('page', String(page));

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error(
            `Candidate enrichment HTTP ${response.status}`
          );
        }

        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(
          html,
          'text/html'
        );
        const resultItems = Array.from(
          parsedDocument.querySelectorAll(
            `${PRODUCT_LIST_SELECTOR} > ${PRODUCT_ITEM_SELECTOR}`
          )
        );

        resultItems.forEach((item) => {
          const handle = item.dataset.essenceSearchHandle || '';

          if (handle && seenHandles.has(handle)) return;
          if (handle) seenHandles.add(handle);
          markup.push(item.outerHTML);
        });

        if (resultItems.length < PRODUCTS_PER_PAGE) break;
      }

      return markup;
    })().catch((error) => {
      candidateMarkupCache.delete(cacheKey);
      throw error;
    });

    candidateMarkupCache.set(cacheKey, requestPromise);
    return requestPromise;
  };

  const fetchPreviewCandidateMarkup = (buyerQuery) => {
    const technicalQuery = buildCandidateQuery(buyerQuery);
    if (!technicalQuery) return Promise.resolve([]);

    if (previewCandidateMarkupCache.has(technicalQuery)) {
      return previewCandidateMarkupCache.get(technicalQuery);
    }

    const requestPromise = (async () => {
      const url = getRootUrl('search');
      url.searchParams.set('q', technicalQuery);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(
          `Preview enrichment HTTP ${response.status}`
        );
      }

      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      return Array.from(
        parsedDocument.querySelectorAll(
          `${PREVIEW_LIST_SELECTOR} > ${PREVIEW_ITEM_SELECTOR}`
        )
      ).map((item) => item.outerHTML);
    })().catch((error) => {
      previewCandidateMarkupCache.delete(technicalQuery);
      throw error;
    });

    previewCandidateMarkupCache.set(technicalQuery, requestPromise);
    return requestPromise;
  };

  const enrichPreview = async (buyerQuery = getPreviewQuery()) => {
    const list = document.querySelector(PREVIEW_LIST_SELECTOR);

    if (
      !(list instanceof HTMLElement) ||
      !buildCandidateQuery(buyerQuery) ||
      list.dataset.essenceSearchEnriched === buyerQuery
    ) {
      return 0;
    }

    list.dataset.essenceSearchEnriched = buyerQuery;

    try {
      const markup = await fetchPreviewCandidateMarkup(buyerQuery);

      if (!list.isConnected || buyerQuery !== getPreviewQuery()) {
        return 0;
      }

      const existingHandles = new Set(
        Array.from(list.children)
          .map((item) => item.dataset.essenceSearchHandle || '')
          .filter(Boolean)
      );
      const importedHandles = new Set();
      const fragment = document.createDocumentFragment();
      let appended = 0;

      markup.forEach((itemMarkup) => {
        const template = document.createElement('template');
        template.innerHTML = itemMarkup.trim();
        const item = template.content.firstElementChild;

        if (!(item instanceof HTMLElement)) return;

        const handle = item.dataset.essenceSearchHandle || '';
        if (handle && existingHandles.has(handle)) return;
        if (handle) importedHandles.add(handle);

        fragment.appendChild(item);
        appended += 1;
      });

      if (appended > 0) {
        list.appendChild(fragment);
      }

      rankPreview();
      return importedHandles.size;
    } catch (error) {
      delete list.dataset.essenceSearchEnriched;
      console.warn(
        '[Essence Search V5] Preview enrichment failed open.',
        error
      );
      return 0;
    }
  };

  const prepareImportedItem = (item) => {
    [
      item,
      ...item.querySelectorAll('[reveal-on-scroll], [reveal]')
    ].forEach((node) => {
      node.removeAttribute('reveal-on-scroll');
      node.removeAttribute('reveal');
      node.removeAttribute('hidden');
      node.style.setProperty('opacity', '1', 'important');
      node.style.setProperty('visibility', 'visible', 'important');
    });

    return item;
  };

  const enrichCandidates = async (buyerQuery = getBuyerQuery()) => {
    if (
      !SEARCH_PATH_RE.test(window.location.pathname) ||
      !CONTEXT_SORTS.has(getActiveSort()) ||
      isIdentifierQuery(buyerQuery)
    ) {
      return 0;
    }

    const section = document.querySelector(SECTION_SELECTOR);
    const productList = getProductList();

    if (
      !(section instanceof HTMLElement) ||
      !(productList instanceof HTMLElement) ||
      section.dataset.essenceSearchEnriched === buyerQuery
    ) {
      return 0;
    }

    section.dataset.essenceSearchEnriched = buyerQuery;
    const sectionId = section.id.replace(/^shopify-section-/u, '');

    try {
      const markup = await fetchCandidateMarkup(
        buyerQuery,
        sectionId
      );

      if (
        !productList.isConnected ||
        buyerQuery !== getBuyerQuery()
      ) {
        return 0;
      }

      const loadedHandles = new Set(
        getProductItems(productList)
          .map((item) => item.dataset.essenceSearchHandle || '')
          .filter(Boolean)
      );
      const existingIndexes = getProductItems(productList)
        .map(getOriginalIndex)
        .filter(Number.isFinite);
      const firstNewIndex = existingIndexes.length
        ? Math.max(...existingIndexes) + 1
        : 0;
      const fragment = document.createDocumentFragment();
      let appended = 0;

      markup.forEach((itemMarkup) => {
        const template = document.createElement('template');
        template.innerHTML = itemMarkup.trim();
        const item = template.content.firstElementChild;

        if (!(item instanceof HTMLElement)) return;

        const handle = item.dataset.essenceSearchHandle || '';
        if (handle && loadedHandles.has(handle)) return;

        item.dataset.essenceSearchOriginalIndex = String(
          firstNewIndex + appended
        );
        prepareImportedItem(item);

        if (handle) loadedHandles.add(handle);
        fragment.appendChild(item);
        appended += 1;
      });

      if (appended > 0) {
        productList.appendChild(fragment);
        document.dispatchEvent(
          new CustomEvent('essence:search:candidates-enriched', {
            detail: { sectionId, count: appended }
          })
        );
      }

      return appended;
    } catch (error) {
      delete section.dataset.essenceSearchEnriched;
      console.warn(
        '[Essence Search V5] Candidate enrichment failed open.',
        error
      );
      return 0;
    }
  };

  const getSortStatus = () => {
    const collectionMain = document.querySelector(
      `${SECTION_SELECTOR} .collection__main`
    );

    if (!(collectionMain instanceof HTMLElement)) return null;

    let status = collectionMain.querySelector(
      `.${SORT_STATUS_CLASS}`
    );

    if (status) return status;

    status = document.createElement('div');
    status.className = SORT_STATUS_CLASS;
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    status.hidden = true;
    status.innerHTML =
      '<span class="essence-search-infinite-scroll__spinner" aria-hidden="true"></span>' +
      '<span class="essence-search-sort-status__text"></span>';

    const productList = collectionMain.querySelector(
      PRODUCT_LIST_SELECTOR
    );

    if (productList) {
      collectionMain.insertBefore(status, productList);
    } else {
      collectionMain.prepend(status);
    }

    return status;
  };

  const setSortBusy = (busy, message = '') => {
    const productList = getProductList();
    const status = getSortStatus();

    if (productList) {
      if (busy) {
        productList.setAttribute('aria-busy', 'true');
      } else {
        productList.removeAttribute('aria-busy');
      }
    }

    if (!status) return;

    status.hidden = !busy && !message;
    status.querySelector(
      '.essence-search-sort-status__text'
    ).textContent = message;
    status.querySelector(
      '.essence-search-infinite-scroll__spinner'
    ).hidden = !busy;
  };

  const waitForInfiniteScrollApi = async () => {
    const startedAt = Date.now();

    while (
      !window.EssenceSearchInfiniteScroll &&
      Date.now() - startedAt < 3000
    ) {
      await new Promise((resolve) => window.setTimeout(resolve, 30));
    }

    return window.EssenceSearchInfiniteScroll || null;
  };

  const getBestSellingCacheKey = (query) =>
    `essence-search-best-selling-v5:${normalizeWhitespace(query)}`;

  const readBestSellingCache = (query) => {
    try {
      const value = window.sessionStorage.getItem(
        getBestSellingCacheKey(query)
      );
      if (!value) return null;

      const parsed = JSON.parse(value);

      if (
        !parsed ||
        !Array.isArray(parsed.handles) ||
        Date.now() - Number(parsed.savedAt) >
          BEST_SELLING_CACHE_TTL
      ) {
        window.sessionStorage.removeItem(
          getBestSellingCacheKey(query)
        );
        return null;
      }

      return new Map(
        parsed.handles.map((handle, index) => [handle, index])
      );
    } catch (error) {
      return null;
    }
  };

  const writeBestSellingCache = (query, handles) => {
    try {
      window.sessionStorage.setItem(
        getBestSellingCacheKey(query),
        JSON.stringify({ savedAt: Date.now(), handles })
      );
    } catch (error) {
      // Best-selling still works when sessionStorage is unavailable.
    }
  };

  const fetchBestSellingRanks = (buyerQuery) => {
    const query = normalizeWhitespace(buyerQuery);
    const cached = readBestSellingCache(query);
    if (cached) return Promise.resolve(cached);

    if (bestSellingPromiseCache.has(query)) {
      return bestSellingPromiseCache.get(query);
    }

    const requestPromise = (async () => {
      const operation = `
        query EssenceSearchBestSelling(
          $first: Int!
          $after: String
          $query: String!
        ) {
          products(
            first: $first
            after: $after
            query: $query
            sortKey: BEST_SELLING
          ) {
            nodes {
              handle
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      `;

      const handles = [];
      let after = null;
      let hasNextPage = true;
      let pages = 0;

      while (hasNextPage && pages < 40) {
        const response = await fetch(STOREFRONT_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: operation,
            variables: {
              first: 250,
              after,
              query
            }
          })
        });
        const payload = await response.json();

        if (
          !response.ok ||
          payload.errors ||
          !payload.data?.products
        ) {
          throw new Error('Shopify BEST_SELLING query failed.');
        }

        payload.data.products.nodes.forEach((product) => {
          if (product?.handle) handles.push(product.handle);
        });

        hasNextPage = Boolean(
          payload.data.products.pageInfo?.hasNextPage
        );
        after =
          payload.data.products.pageInfo?.endCursor || null;
        pages += 1;
      }

      writeBestSellingCache(query, handles);
      return new Map(
        handles.map((handle, index) => [handle, index])
      );
    })().catch((error) => {
      bestSellingPromiseCache.delete(query);
      throw error;
    });

    bestSellingPromiseCache.set(query, requestPromise);
    return requestPromise;
  };

  const setSelectedOption = (sortValue) => {
    document
      .querySelectorAll(
        `${SECTION_SELECTOR} facets-sort-popover [role="option"][value]`
      )
      .forEach((option) => {
        if (option.getAttribute('value') === sortValue) {
          option.setAttribute('aria-selected', 'true');
        } else {
          option.removeAttribute('aria-selected');
        }
      });
  };

  const updateSortUrl = (sortValue) => {
    const url = new URL(window.location.href);

    if (!sortValue || sortValue === 'relevance') {
      url.searchParams.delete('sort_by');
    } else {
      url.searchParams.set('sort_by', sortValue);
    }

    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    window.history.replaceState({}, '', url.toString());
  };

  const closeSortPopover = () => {
    document.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Escape',
        bubbles: true
      })
    );
  };

  const applyGlobalSort = async (sortValue) => {
    if (!GLOBAL_SORTS.has(sortValue)) return;

    const section = document.querySelector(SECTION_SELECTOR);
    if (!(section instanceof HTMLElement)) return;

    const currentItemCount = getProductItems().length;

    if (
      section.dataset.essenceSearchGlobalSort === sortValue &&
      Number(section.dataset.essenceSearchGlobalSortCount) ===
        currentItemCount
    ) {
      setSelectedOption(sortValue);
      return;
    }

    const activeSort = activeGlobalSortPromises.get(section);

    if (activeSort) {
      if (activeSort.sortValue === sortValue) {
        return activeSort.promise;
      }

      return activeSort.promise.finally(() =>
        applyGlobalSort(sortValue)
      );
    }

    const requestPromise = (async () => {
      const buyerQuery = getBuyerQuery();
      setSortBusy(
        true,
        'טוען את כל תוצאות החיפוש כדי למיין בצורה מדויקת…'
      );

      try {
        const infiniteScroll = await waitForInfiniteScrollApi();

        if (!infiniteScroll?.loadAll) {
          throw new Error('Infinite Scroll API is unavailable.');
        }

        const loadResult = await infiniteScroll.loadAll();

        if (!loadResult.complete) {
          throw new Error('The complete candidate set was not loaded.');
        }

        let bestSellingRanks = null;

        if (sortValue === 'best-selling') {
          setSortBusy(true, 'מחשב את סדר המוצרים הנמכרים ביותר…');
          bestSellingRanks = await fetchBestSellingRanks(buyerQuery);
        }

        const productList = getProductList();
        const items = getProductItems(productList);

        if (!(productList instanceof HTMLElement) || !items.length) {
          return;
        }

        items.sort((a, b) => {
          const originalDifference =
            getOriginalIndex(a) - getOriginalIndex(b);

          switch (sortValue) {
            case 'title-ascending':
              return (
                collator.compare(
                  a.dataset.essenceSearchTitle || '',
                  b.dataset.essenceSearchTitle || ''
                ) || originalDifference
              );

            case 'title-descending':
              return (
                collator.compare(
                  b.dataset.essenceSearchTitle || '',
                  a.dataset.essenceSearchTitle || ''
                ) || originalDifference
              );

            case 'created-ascending':
              return (
                Number(a.dataset.essenceSearchCreated || 0) -
                  Number(b.dataset.essenceSearchCreated || 0) ||
                originalDifference
              );

            case 'created-descending':
              return (
                Number(b.dataset.essenceSearchCreated || 0) -
                  Number(a.dataset.essenceSearchCreated || 0) ||
                originalDifference
              );

            case 'best-selling': {
              const handleA = a.dataset.essenceSearchHandle || '';
              const handleB = b.dataset.essenceSearchHandle || '';
              const rankA = bestSellingRanks?.has(handleA)
                ? bestSellingRanks.get(handleA)
                : Number.MAX_SAFE_INTEGER;
              const rankB = bestSellingRanks?.has(handleB)
                ? bestSellingRanks.get(handleB)
                : Number.MAX_SAFE_INTEGER;

              return rankA - rankB || originalDifference;
            }

            default:
              return originalDifference;
          }
        });

        reorder(productList, items);
        setSelectedOption(sortValue);
        section.dataset.essenceSearchGlobalSort = sortValue;
        section.dataset.essenceSearchGlobalSortCount = String(
          items.length
        );
        setSortBusy(false);
      } catch (error) {
        console.error('[Essence Search V5] Global sort failed.', error);
        setSortBusy(
          false,
          'לא הצלחנו להשלים מיון מלא. סדר התוצאות הקיים נשמר.'
        );
        window.setTimeout(() => setSortBusy(false), 5000);
      } finally {
        const activeSort = activeGlobalSortPromises.get(section);

        if (activeSort?.promise === requestPromise) {
          activeGlobalSortPromises.delete(section);
        }
      }
    })();

    activeGlobalSortPromises.set(section, {
      sortValue,
      promise: requestPromise
    });
    return requestPromise;
  };

  const handleSortChange = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const sortPopover = target.closest('facets-sort-popover');

    if (
      !sortPopover ||
      !sortPopover.closest(SECTION_SELECTOR)
    ) {
      return;
    }

    const sortValue = event.detail?.value || 'relevance';

    if (!GLOBAL_SORTS.has(sortValue) && sortValue !== 'manual') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    updateSortUrl(sortValue);
    setSelectedOption(sortValue);
    closeSortPopover();

    if (sortValue === 'manual') {
      restoreNativeOrder();
      applyContextRanking(getBuyerQuery(), sortValue);
      return;
    }

    void applyGlobalSort(sortValue);
  };

  const initialize = async () => {
    const version = ++lifecycleVersion;
    const previewQuery = getPreviewQuery();

    if (previewQuery) {
      await enrichPreview(previewQuery);
    }

    rankPreview();

    if (!SEARCH_PATH_RE.test(window.location.pathname)) return;

    const buyerQuery = getBuyerQuery();
    const sortValue = getActiveSort();

    if (!buyerQuery) return;

    if (CONTEXT_SORTS.has(sortValue)) {
      if (sortValue === 'manual') restoreNativeOrder();
      await enrichCandidates(buyerQuery);

      if (version !== lifecycleVersion) return;
      applyContextRanking(buyerQuery, sortValue);
      setSelectedOption(sortValue);
    } else if (GLOBAL_SORTS.has(sortValue)) {
      await applyGlobalSort(sortValue);
    }
  };

  const scheduleInitialize = () => {
    window.clearTimeout(scheduleTimer);
    scheduleTimer = window.setTimeout(() => {
      void initialize();
    }, 0);
  };

  document.addEventListener('listbox:change', handleSortChange, true);
  document.addEventListener(
    'essence:search:products-appended',
    scheduleInitialize
  );
  document.addEventListener(
    'essence:search:candidates-enriched',
    scheduleInitialize
  );
  document.addEventListener('shopify:section:load', scheduleInitialize);
  document.documentElement.addEventListener(
    'theme:loading:end',
    () => window.requestAnimationFrame(scheduleInitialize)
  );
  window.addEventListener('popstate', scheduleInitialize);

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      scheduleInitialize,
      { once: true }
    );
  } else {
    scheduleInitialize();
  }

  window.EssenceSearchV5 = Object.freeze({
    version: '5.0.0',
    normalizeWhitespace,
    normalizeToken,
    tokenize,
    isIdentifierQuery,
    buildVariants,
    buildCandidateQuery,
    getContextScore,
    enrichPreview,
    enrichCandidates,
    applyContextRanking,
    applyGlobalSort,
    refresh: scheduleInitialize
  });
})();